import React, { useState, useEffect, useRef } from "react";
import { speakers, moderator } from "../data/mock";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isVisible];
};

const mono = "'JetBrains Mono', monospace";
const sans = "'Inter', Arial, sans-serif";
const heading = "'Montserrat', Arial, sans-serif";

export const SpeakersSection = () => {
  const [headerRef, headerVisible] = useInView();
  const [gridRef, gridVisible] = useInView();
  const [mobileRef, mobileVisible] = useInView();
  const [modRef, modVisible] = useInView();

  return (
    <section
      id="speakers"
      className="py-16 md:py-[120px] relative"
      style={{
        background: "white",
        backgroundImage: "radial-gradient(circle, #e5e7eb 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    >
      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className="mb-10 md:mb-16 transition-all duration-700"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(30px)",
          }}
        >
          <span
            className="block text-[#9ca3af] text-sm mb-4"
            style={{ fontFamily: mono }}
          >
            // Speakers
          </span>
          <h2
            className="text-[#FE3A80] uppercase leading-[0.85] mb-4"
            style={{
              fontFamily: heading,
              fontWeight: 900,
              fontSize: "clamp(2rem, 1.5rem + 2vw, 3rem)",
            }}
          >
            The Speakers
          </h2>
          <p
            className="text-[#4b5563] text-lg font-medium leading-relaxed max-w-2xl"
            style={{ fontFamily: sans }}
          >
            Three accomplished women with powerful stories and real-world credibility.
          </p>
        </div>

        {/* ── Desktop: Three Equal Speaker Cards ── */}
        <div
          ref={gridRef}
          className="hidden md:grid border border-[#e5e7eb]"
          style={{
            gridTemplateColumns: "repeat(3, 1fr)",
          }}
        >
          {speakers.map((speaker, i) => (
            <div
              key={i}
              className="group relative flex flex-col overflow-hidden transition-colors duration-500 hover:bg-[#fafbfc]"
              style={{
                borderLeft: i > 0 ? "1px solid #e5e7eb" : "none",
                opacity: gridVisible ? 1 : 0,
                transform: gridVisible ? "translateY(0)" : "translateY(16px)",
                transition:
                  "opacity 500ms, transform 500ms, background-color 500ms",
                transitionDelay: gridVisible ? `${i * 100}ms` : "0ms",
              }}
            >
              {/* Photo */}
              <div className="relative overflow-hidden" style={{ height: 340 }}>
                <img
                  src={speaker.image}
                  alt={speaker.name}
                  className="w-full h-full object-cover object-top absolute inset-0 transition-transform duration-700 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
              {/* Text */}
              <div className="p-6 flex flex-col flex-1">
                <span
                  className="text-[#9ca3af] text-xs block mb-3"
                  style={{ fontFamily: mono }}
                >
                  {`0${i + 1}`}
                </span>
                <h3
                  className="text-[#FE3A80] uppercase mb-2"
                  style={{
                    fontFamily: heading,
                    fontWeight: 900,
                    fontSize: "1.25rem",
                    lineHeight: 1,
                  }}
                >
                  {speaker.name}
                </h3>
                <p
                  className="text-[#6b7280] text-xs uppercase tracking-[0.15em] font-medium mb-3"
                  style={{ fontFamily: sans }}
                >
                  {speaker.role}
                </p>
                <div className="w-8 h-[1px] bg-[#e5e7eb] mb-3" />
                <p
                  className="text-[#4b5563] text-sm leading-relaxed"
                  style={{ fontFamily: sans }}
                >
                  {speaker.description}
                </p>
              </div>
              {/* Accent stripe bottom */}
              <div className="h-[2px] bg-[#FE3A80]" />
            </div>
          ))}
        </div>

        {/* ── Mobile: Three Equal Stacked Cards ── */}
        <div ref={mobileRef} className="md:hidden border border-[#e5e7eb]">
          {speakers.map((speaker, i) => (
            <div
              key={i}
              className="relative overflow-hidden transition-all duration-500"
              style={{
                height: 340,
                borderTop: i > 0 ? "1px solid #e5e7eb" : "none",
                opacity: mobileVisible ? 1 : 0,
                transform: mobileVisible
                  ? "translateX(0)"
                  : "translateX(-16px)",
                transitionDelay: mobileVisible ? `${i * 80}ms` : "0ms",
              }}
            >
              <img
                src={speaker.image}
                alt={speaker.name}
                className="w-full h-full object-cover object-top absolute inset-0"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 50%, transparent 70%)",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span
                  className="text-white/50 text-xs block mb-2"
                  style={{ fontFamily: mono }}
                >
                  {`0${i + 1}`}
                </span>
                <h3
                  className="text-white uppercase text-lg mb-1"
                  style={{ fontFamily: heading, fontWeight: 900, lineHeight: 1 }}
                >
                  {speaker.name}
                </h3>
                <p
                  className="text-white/70 text-xs uppercase tracking-[0.15em] font-medium"
                  style={{ fontFamily: sans }}
                >
                  {speaker.role}
                </p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FE3A80]" />
            </div>
          ))}
        </div>

        {/* ── Moderator & MC — Separate Section ── */}
        <div
          ref={modRef}
          className="mt-10 md:mt-16 border border-[#e5e7eb] flex flex-col md:flex-row overflow-hidden transition-all duration-700"
          style={{
            background: "#fafbfc",
            opacity: modVisible ? 1 : 0,
            transform: modVisible ? "translateY(0)" : "translateY(20px)",
          }}
        >
          {/* Photo — natural aspect ratio, text side stretches to match */}
          <div className="w-full md:w-[340px] shrink-0 overflow-hidden">
            <img
              src={moderator.image}
              alt={moderator.name}
              className="w-full h-auto block object-cover object-top"
              loading="lazy"
            />
          </div>
          {/* Text */}
          <div className="relative flex-1 p-6 md:p-10 pl-8 md:pl-12 flex flex-col justify-center">
            {/* Accent stripe left */}
            <div className="absolute left-0 top-6 bottom-6 w-[2px] bg-[#FE3A80]" />
            <span
              className="text-[#9ca3af] text-sm block mb-4"
              style={{ fontFamily: mono }}
            >
              // Moderator & MC
            </span>
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-3">
              <h3
                className="text-[#FE3A80] uppercase"
                style={{
                  fontFamily: heading,
                  fontWeight: 900,
                  fontSize: "clamp(1.25rem, 1rem + 1vw, 1.75rem)",
                  lineHeight: 1,
                }}
              >
                {moderator.name}
              </h3>
              <span
                className="inline-block self-start text-[11px] text-[#6b7280] border border-[#e5e7eb] rounded-full px-2.5 py-0.5 font-medium"
                style={{ fontFamily: mono }}
              >
                {moderator.role}
              </span>
            </div>
            <p
              className="text-[#4b5563] text-sm md:text-base leading-relaxed max-w-2xl"
              style={{ fontFamily: sans }}
            >
              {moderator.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
